import React, { useState, useEffect, useContext } from "react";
import {
  Drawer,
  Form,
  Upload,
  message,
  Space,
  Button as ANTDButton,
  Progress,
  Card,
  List,
  Tag,
  Spin,
  Typography,
  Divider,
  Tooltip,
  Row,
} from "antd";
import {
  InboxOutlined,
  CloseOutlined,
  EyeOutlined,
  DeleteOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import axios, { CancelTokenSource } from "axios";
import AssetViewerModal from "./AssetViewerModal";
import {
  deleteUploadedAssetOnLecture,
  fetchPresignedUrl,
  fetchUploadedAssetOnLecture,
  GET_CONTENT_LICENCE,
  uploadCallbackUrl,
} from "utilFunctions/Constants/urlConstants";
import {
  getAPIRequest,
  putPostRequest,
  deleteAPI,
} from "utilFunctions/Axios/AxiosRequestConfiguration";
import { ToastContextDispatch } from "Context/CommonContext";
import { useGetApi } from "utilFunctions/CustomHooks/useGetAPI";
import useWindowSize from "utilFunctions/CustomHooks/useWindowSize";
const { Text } = Typography;
interface UploadAssetDrawerProps {
  open: boolean;
  onClose: () => void;
  onUploadSuccess: (data: any) => void;
  BASE_URL: string;
  lectureId?: string;
  courseId?: string;
}

interface FileProgress {
  file: File;
  progress: number;
  cancelToken: CancelTokenSource;
  remainingTime: string;
  isUploading: boolean;
}

const returnTagColor = (status: string) => {
  switch (status) {
    case "Processing":
      return "blue";
    case "Uploading":
      return "orange";
    case "Uploaded":
      return "cyan";
    case "Failed":
      return "red";
    default:
      return "green";
  }
};

const UploadAssetDrawer: React.FC<UploadAssetDrawerProps> = ({
  open,
  onClose,
  onUploadSuccess,
  BASE_URL,
  lectureId,
  courseId,
}) => {
  const [form] = Form.useForm();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [selectedVideo, setSelectedVideo] = useState<any>(null);
  const [videoProgress, setVideoProgress] = useState<number>(0);
  const [isVideoUploading, setIsVideoUploading] = useState<boolean>(false);
  const [isPDFUploading, setIsPDFUploading] = useState<boolean>(false);
  const [videoCancelToken, setVideoCancelToken] =
    useState<CancelTokenSource | null>(null);
  const ToastDispatch = useContext(ToastContextDispatch);
  const { screen }: any = useWindowSize();

  const [videoData, setVideoData] = useState<any>(null);
  const [pdfData, setPdfData] = useState<any[]>([]);
  const [pdfUploads, setPdfUploads] = useState<FileProgress[]>([]);

  const [isViewerModalOpen, setIsViewerModalOpen] = useState(false);
  const [viewerContentType, setViewerContentType] = useState<{
    type: "video" | "ebook" | null;
    data: string;
  } | null>(null);
  const [viewerTitle, setViewerTitle] = useState<string | undefined>("");
  const [viewerAssetId, setViewerAssetId] = useState<string | undefined>("");
  const [pdfPassword, setPdfPassword] = useState<string>("");

  const { makeRequest: fetchFileDecryptionKey } = useGetApi(
    (data: any) => {
      if (data?.detailMessage) {
        const decodedKey = atob(data?.detailMessage);
        setPdfPassword(decodedKey);
      } else {
        setPdfPassword("NO_PASSWORD");
      }
    },
    (error: any) => {
      setPdfPassword("NO_PASSWORD");
      console.error(error);
    }
  );

  useEffect(() => {
    if (open) {
      fetchUploadedFiles();
    }
  }, [open]);

  const fetchUploadedFiles = async () => {
    try {
      getAPIRequest({
        url: `${BASE_URL + fetchUploadedAssetOnLecture}?id=${lectureId}`,
        success: (response: any) => {
          if (response.data?.data) {
            const video = response.data.data.find(
              (item: any) => item.assetType === "Video"
            );
            const pdf = response.data.data.filter(
              (item: any) => item.assetType === "Ebook"
            );
            if (video) {
              setVideoData(video);
            } else {
              setVideoData(null);
            }
            setPdfData([...pdf]);

            // Check if any asset is still processing
            const isProcessing = response.data.data.some(
              (item: any) => item.status === "Processing"
            );

            // If any asset is processing, call the function again after 5 seconds
            if (isProcessing) {
              setTimeout(fetchUploadedFiles, 5000);
            }
          }
        },
        failure: (data: any) => {
          ToastDispatch({
            isOpen: true,
            type: "Error",
            message: data["message"],
          });
        },
      });
    } catch (error) {
      message.error("Failed to fetch uploaded files");
    }
  };

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const SaveButton = async () => {
    if (!selectedVideo && pdfUploads.length === 0) {
      return message.error("Please select at least one file (Video or PDFs)");
    }

    setIsSaving(true);

    const uploadPromises: Promise<void>[] = [];

    // Upload video if selected
    if (selectedVideo) {
      uploadPromises.push(uploadFile(selectedVideo, "Video"));
    }
    setPdfUploads((prevUploads) =>
      prevUploads.map((upload) => ({
        ...upload,
        isUploading: true,
      }))
    );
    // Upload each PDF
    pdfUploads.forEach((upload) => {
      uploadPromises.push(uploadFile(upload.file, "Ebook", upload.cancelToken));
    });

    // Wait for all uploads and callbacks to complete
    await Promise.allSettled(uploadPromises);

    // Fetch updated data from the server
    await fetchUploadedFiles();

    // Reset saving state
    setIsSaving(false);
    setPdfUploads([]);
    // message.success("Upload process completed!");
  };

  const uploadFile = (
    file: File,
    type: "Video" | "Ebook",
    cancelToken?: CancelTokenSource
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const bodyData: any = {
        fileName: file.name,
        assetType: type,
        lectureId: lectureId,
      };

      const token = cancelToken || axios.CancelToken.source();
      if (type === "Video") {
        setIsVideoUploading(true);
        setVideoCancelToken(token);
      } else {
        setIsPDFUploading(true);
      }

      putPostRequest({
        url: `${BASE_URL + fetchPresignedUrl}`,
        success: (res: any) => {
          if (res?.data?.data) {
            const { preSignedUrl, lectureUploadId } = res.data.data;
            handleUploadOnS3(
              preSignedUrl,
              lectureUploadId,
              file,
              bodyData,
              type,
              token
            )
              .then(resolve) // Resolve after handleUploadOnS3 completes
              .catch(reject); // Reject if handleUploadOnS3 fails
          }
        },
        failure: (err: any) => {
          setIsSaving(false);
          message.error(err.response?.data?.message || `${type} upload failed`);
          reject(err); // Reject the promise on failure
        },
        body: bodyData,
        method: "Post",
      });
    });
  };

  const handleUploadOnS3 = (
    url: string,
    lectureUploadId: string,
    file: File,
    bodyData: any,
    type: "Video" | "Ebook",
    cancelToken: CancelTokenSource
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let fakeProgress = 0;
      const increment = Math.floor(Math.random() * 5 + 3);
      xhr.open("PUT", url, true);
      xhr.setRequestHeader("Content-Type", file.type);

      const progressInterval = setInterval(() => {
        fakeProgress += increment;
        if (fakeProgress >= 85) {
          clearInterval(progressInterval);
        }

        if (type === "Video") {
          setVideoProgress(fakeProgress);
        } else {
          setPdfUploads((prevUploads) =>
            prevUploads.map((upload) =>
              upload.file.name === file.name
                ? { ...upload, progress: fakeProgress }
                : upload
            )
          );
        }
      }, 500); // Update progress every 500ms

      // Handle upload completion
      xhr.onload = () => {
        clearInterval(progressInterval); // Stop fake progress
        if (xhr.status === 200) {
          if (type === "Video") {
            setVideoProgress(100); // Set progress to 100% on success
          } else {
            setPdfUploads((prevUploads) =>
              prevUploads.map((upload) =>
                upload.file.name === file.name
                  ? { ...upload, progress: 100, isUploading: false }
                  : upload
              )
            );
          }
          handleURLCallback(bodyData, lectureUploadId, type, file.name)
            .then(resolve)
            .catch(reject);
        } else {
          message.error(`${type.toUpperCase()}: ${file.name} upload failed`);
          deleteFile(
            type.toLowerCase() as "video" | "pdf",
            lectureUploadId,
            file.name
          );
          reject(new Error(`Failed to upload ${type}: ${file.name}`));
        }
      };

      // Handle upload error
      xhr.onerror = () => {
        clearInterval(progressInterval); // Stop fake progress
        message.error(`${type.toUpperCase()}: ${file.name} upload failed`);
        deleteFile(
          type.toLowerCase() as "video" | "pdf",
          lectureUploadId,
          file.name
        );
        reject(new Error(`Failed to upload ${type}: ${file.name}`));
      };

      // Handle upload cancellation
      cancelToken.token.promise.then(() => {
        xhr.abort();
        clearInterval(progressInterval); // Stop fake progress
        message.info(`${type.toUpperCase()} upload canceled`);
        if (type === "Video") {
          setIsVideoUploading(false);
          deleteFile("video", lectureUploadId);
          form.setFieldValue("video", null); // Reset the video field in the form
        } else {
          setPdfUploads((prevUploads) =>
            prevUploads.map((upload) =>
              upload.file.name === file.name
                ? { ...upload, progress: 0, isUploading: false }
                : upload
            )
          );
          deleteFile("pdf", lectureUploadId, file.name);
        }
        reject(new Error("Upload canceled"));
      });

      // Start the upload
      xhr.send(file);
    });
  };

  const handleURLCallback = (
    data: any,
    lectureUploadId: string,
    type: "Video" | "Ebook",
    fileName?: string
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const bodydata = {
        ...data,
        lectureUploadId: lectureUploadId,
      };
      putPostRequest({
        url: `${BASE_URL + uploadCallbackUrl}`,
        success: (res: any) => {
          if (type === "Video") {
            setVideoProgress(0);
            setSelectedVideo(null);
            setVideoCancelToken(null);
            setIsVideoUploading(false);
            form.setFieldValue("video", null); // Reset the video field in the form
            const vidData = {
              ...data,
              lectureUploadId: lectureUploadId,
              status: "Uploaded",
              fileName: fileName,
            };
            setVideoData(vidData); // Update video data
            message.success(`${type} Upload process completed!`);
          } else {
            const pdfData = {
              ...data,
              lectureUploadId: lectureUploadId,
              status: "Uploaded",
              fileName: fileName,
            };
            form.setFieldValue("pdf", null); // Reset the PDF field in the form
            setIsPDFUploading(false);
            setPdfUploads((prevUploads) =>
              prevUploads.filter((upload) => upload.file.name !== fileName)
            );
            setPdfData((prevData) => [...prevData, pdfData]); // Add uploaded PDF data
            message.success(`${type} Upload process completed!`);
          }
          setIsSaving(false);
          onUploadSuccess(res.data);
          resolve(); // Resolve the promise on success
        },
        failure: (err: any) => {
          if (type === "Video") {
            setVideoProgress(0);
            setSelectedVideo(null);
            setVideoCancelToken(null);
            setIsVideoUploading(false);
          } else {
            setIsPDFUploading(false);
            setPdfUploads((prevUploads) =>
              prevUploads.filter((upload) => upload.file.name !== fileName)
            );
          }
          setIsSaving(false);
          message.error(err.response?.data?.message || "Callback failed");
          reject(err); // Reject the promise on failure
        },
        body: bodydata,
        method: "Post",
      });
    });
  };

  const cancelUpload = (type: "video" | "pdf", fileName?: string) => {
    if (type === "video" && videoCancelToken) {
      videoCancelToken.cancel();
      setVideoProgress(0);
      setSelectedVideo(null);
      setVideoCancelToken(null);
      form.setFieldValue("video", null);
    } else if (type === "pdf" && fileName) {
      setPdfUploads((prevUploads) => {
        const uploadToCancel = prevUploads.find(
          (upload) => upload.file.name === fileName
        );
        if (uploadToCancel) {
          uploadToCancel.cancelToken.cancel();
        }
        return prevUploads.filter((upload) => upload.file.name !== fileName);
      });
    }
  };

  // const formatTime = (seconds: number): string => {
  //   if (isNaN(seconds) || seconds <= 0) return "Calculating...";
  //   const minutes = Math.floor(seconds / 60);
  //   const remainingSeconds = Math.floor(seconds % 60);
  //   return `${minutes}m ${remainingSeconds}s`;
  // };

  const deleteFile = async (
    type: "video" | "pdf",
    id: any,
    fileName?: string
  ) => {
    try {
      if (type === "video") {
        // Delete video file
        deleteAPI({
          url: `${BASE_URL + deleteUploadedAssetOnLecture}?id=${id}`,
          success: () => {
            message.success(`${type.toUpperCase()} file deleted successfully`);
            setVideoData(null);
            setSelectedVideo(null);
            setVideoProgress(0);
            form.setFieldValue("video", null);
            setIsVideoUploading(false);
          },
          failure: (error: any) => {
            message.error(
              error.response?.data?.message || `Failed to delete ${type} file`
            );
          },
          body: { fileType: type },
        });
      } else if (type === "pdf" && fileName) {
        // Delete specific PDF file
        deleteAPI({
          url: `${BASE_URL + deleteUploadedAssetOnLecture}?id=${id}`,
          success: () => {
            message.success(`PDF file "${fileName}" deleted successfully`);
            setPdfData((prevData) =>
              prevData.filter((pdf) => pdf.fileName !== fileName)
            );
          },
          failure: (error: any) => {
            message.error(
              error.response?.data?.message || "Failed to delete PDF file"
            );
          },
          body: { fileType: type, fileName },
        });
      }
    } catch (error) {
      message.error(`Unexpected error occurred while deleting ${type} file`);
    }
  };

  const handleClose = () => {
    // Reset the form fields
    form.resetFields();
    setPdfData([]);
    setVideoData(null);
    // Clear state variables
    setSelectedVideo(null);
    setVideoProgress(0);
    setVideoCancelToken(null);
    setPdfUploads([]);
    setIsSaving(false);

    // Call the parent-provided onClose function
    onClose();
  };

  const openViewerModal = async (
    type: "video" | "ebook",
    data: string,
    title?: string,
    assetId?: string
  ) => {
    if (type === "ebook" && assetId) {
      // Fetch the PDF password
      await fetchFileDecryptionKey(
        BASE_URL +
          GET_CONTENT_LICENCE +
          `?assetId=${lectureId}&courseId=${courseId}&source=FEX`
      );
    }

    // Open the modal after fetching the password
    setViewerContentType({ type, data });
    setViewerTitle(title);
    setViewerAssetId(assetId);
    setIsViewerModalOpen(true);
  };

  const closeViewerModal = () => {
    setViewerContentType(null);
    setViewerTitle("");
    setViewerAssetId("");
    setIsViewerModalOpen(false);
  };
  console.log(
    isPDFUploading,
    viewerAssetId,
    "contentType",
    pdfUploads,
    selectedVideo,
    videoProgress
  );

  return (
    <>
      <Drawer
        title="Upload Lecture Recording and Lecture notes"
        width={600}
        closable={true}
        open={open}
        onClose={handleClose}
        footer={
          <Space style={{ display: "flex", justifyContent: "flex-end" }}>
            <ANTDButton onClick={handleClose}>Cancel</ANTDButton>
            <ANTDButton
              loading={
                !!(
                  isVideoUploading ||
                  (pdfUploads.length &&
                    pdfUploads.some((upload) => upload.isUploading))
                )
              }
              type="primary"
              htmlType="submit"
              onClick={SaveButton}
              disabled={!selectedVideo && pdfUploads.length === 0}
            >
              {isSaving ? "Saving..." : "Save"}
            </ANTDButton>
          </Space>
        }
      >
        <Form
          layout="vertical"
          labelWrap
          form={form}
          name="control-upload"
          onFinish={SaveButton}
        >
          {videoData ? (
            <Card title="Video File" style={{ marginBottom: 16 }}>
              <List
                itemLayout="horizontal"
                dataSource={[videoData]}
                renderItem={(video) => (
                  <List.Item
                    style={{ display: "flex", alignItems: "center" }}
                    actions={[
                      video.status == "Live" && (
                        <ANTDButton
                          key={video.fileName + "view"}
                          type="link"
                          icon={<EyeOutlined />}
                          onClick={() =>
                            openViewerModal(
                              "video",
                              video.lectureContentUrl,
                              video.fileName,
                              video.lectureUploadId
                            )
                          }
                        ></ANTDButton>
                      ),
                      <ANTDButton
                        key={video.fileName + "delete"}
                        type="link"
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() =>
                          deleteFile("video", video.lectureUploadId)
                        }
                      ></ANTDButton>,
                    ]}
                  >
                    <Row
                      style={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Tooltip title={video.fileName}>
                        <span
                          style={{
                            fontWeight: "bold",
                            marginRight: "10px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: ["xs", "sm", "md"].includes(screen)
                              ? "50%"
                              : "70%",
                          }}
                        >
                          {video.fileName}
                        </span>
                      </Tooltip>

                      <Tag color={returnTagColor(video.status)}>
                        {video.status}
                      </Tag>
                    </Row>
                  </List.Item>
                )}
              />
            </Card>
          ) : (
            <>
              {videoProgress > 0 || isVideoUploading ? (
                <Card style={{ marginBottom: 16 }}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "flex-start",
                    }}
                  >
                    <Spin size="large" indicator={<LoadingOutlined spin />} />
                    <div style={{ width: "100%", marginLeft: "10px" }}>
                      <Text>{selectedVideo?.name}</Text>
                      {videoProgress > 0 && (
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                          }}
                        >
                          <Progress
                            percent={videoProgress}
                            status={
                              videoProgress === 100 ? "success" : "active"
                            }
                            style={{ width: "100%" }}
                          />
                          <ANTDButton
                            type="text"
                            icon={<CloseOutlined />}
                            onClick={() => cancelUpload("video")}
                            style={{ marginLeft: "10px" }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ) : (
                <Form.Item
                  name="video"
                  valuePropName="fileList"
                  getValueFromEvent={normFile}
                  rules={[
                    {
                      required: !selectedVideo,
                      message: "Please choose a video file",
                    },
                  ]}
                >
                  <Upload.Dragger
                    name="video"
                    accept="video/mp4"
                    multiple={false}
                    maxCount={1}
                    onRemove={() => {
                      setSelectedVideo(null);
                      setVideoProgress(0);
                      setVideoCancelToken(null);
                      setIsVideoUploading(false);
                      form.setFieldValue("video", null);
                    }}
                    beforeUpload={(file) => {
                      const isLt2GB = file.size / 1024 / 1024 < 500; // 2GB limit
                      if (file.type !== "video/mp4") {
                        message.error("You can only upload video files!");
                      }
                      if (!isLt2GB) {
                        message.error("Video must be smaller than 500MB!");
                      } else {
                        setSelectedVideo(file);
                      }
                      return false; // Prevent auto-upload
                    }}
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">Select a Video to upload</p>
                    <p className="ant-upload-hint">
                      Maximum file size 500MB, format .mp4
                    </p>
                  </Upload.Dragger>
                </Form.Item>
              )}
            </>
          )}

          {pdfData.length > 0 ? (
            <Card title="PDF Files">
              <>
                <List
                  itemLayout="horizontal"
                  dataSource={pdfData}
                  renderItem={(pdf) => (
                    <List.Item
                      style={{ display: "flex", alignItems: "center" }}
                      actions={[
                        pdf.status == "Live" && (
                          <ANTDButton
                            key={pdf.fileName + "view"}
                            type="link"
                            icon={<EyeOutlined />}
                            onClick={() =>
                              openViewerModal(
                                "ebook",
                                pdf.lectureContentUrl,
                                pdf.fileName,
                                pdf.lectureUploadId
                              )
                            } // Open modal with PDF data
                          ></ANTDButton>
                        ),
                        <ANTDButton
                          key={pdf.fileName + "delete"}
                          type="link"
                          icon={<DeleteOutlined />}
                          danger
                          onClick={() =>
                            deleteFile("pdf", pdf.lectureUploadId, pdf.fileName)
                          }
                        ></ANTDButton>,
                      ]}
                    >
                      <Row
                        style={{
                          flex: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Tooltip title={pdf.fileName}>
                          <span
                            style={{
                              fontWeight: "bold",
                              marginRight: "10px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: ["xs", "sm", "md"].includes(screen)
                                ? "50%"
                                : "70%",
                            }}
                          >
                            {pdf.fileName}
                          </span>
                        </Tooltip>
                        <Tag color={returnTagColor(pdf.status)}>
                          {pdf.status}
                        </Tag>
                      </Row>
                    </List.Item>
                  )}
                />
                <div style={{ marginTop: 16 }}>
                  <Upload
                    name="pdf"
                    accept="application/pdf"
                    multiple={true}
                    maxCount={10}
                    showUploadList={false}
                    onRemove={(file) => {
                      setPdfUploads((prevUploads) =>
                        prevUploads.filter(
                          (upload) => upload.file.name !== file.name
                        )
                      );
                    }}
                    beforeUpload={async (file, fileList) => {
                      if (fileList.length > 10) {
                        return Upload.LIST_IGNORE;
                      }

                      const isLt10MB = file.size / 1024 / 1024 < 50;
                      if (file.type !== "application/pdf") {
                        message.error("You can only upload PDF files!");
                        return Upload.LIST_IGNORE;
                      }
                      if (!isLt10MB) {
                        message.error("PDF must be smaller than 50MB!");
                        return Upload.LIST_IGNORE;
                      }

                      // Trigger the uploadFile function directly
                      const cancelToken = axios.CancelToken.source();
                      setPdfUploads((prevUploads) => [
                        ...prevUploads,
                        {
                          file,
                          progress: 0,
                          cancelToken,
                          remainingTime: "Calculating...",
                          isUploading: true,
                        },
                      ]);

                      // Trigger the uploadFile function directly
                      uploadFile(file, "Ebook", cancelToken)
                        .then(() => {
                          // Fetch updated data from the server after upload
                          fetchUploadedFiles();
                        })
                        .catch(() => {
                          // Handle upload failure
                          setPdfUploads((prevUploads) =>
                            prevUploads.filter(
                              (upload) => upload.file.name !== file.name
                            )
                          );
                        });

                      return false; // Prevent auto-upload by Upload component
                    }}
                  >
                    <ANTDButton type="dashed" icon={<InboxOutlined />}>
                      Upload More
                    </ANTDButton>
                  </Upload>
                </div>
              </>
            </Card>
          ) : (
            <Form.Item
              name="pdf"
              valuePropName="fileList"
              getValueFromEvent={normFile}
              rules={[
                {
                  validator: () => {
                    if (pdfUploads.length > 10) {
                      return Promise.reject(
                        new Error("You can only upload up to 10 PDF files!")
                      );
                    }
                    return Promise.resolve();
                  },
                },
                {
                  required: !pdfUploads.length,
                  message: "Please choose PDFs files",
                },
              ]}
            >
              {!isSaving && (
                <Upload.Dragger
                  name="pdf"
                  accept="application/pdf"
                  multiple={true}
                  maxCount={10}
                  onRemove={(file) => {
                    setPdfUploads((prevUploads) =>
                      prevUploads.filter(
                        (upload) => upload.file.name !== file.name
                      )
                    );
                  }}
                  beforeUpload={(file, fileList) => {
                    if (fileList.length > 10) {
                      ToastDispatch({
                        isOpen: true,
                        type: "Error",
                        message: "You can only upload up to 10 PDF files!",
                      });
                      return Upload.LIST_IGNORE;
                    }
                    const isLt10MB = file.size / 1024 / 1024 < 50;
                    if (file.type !== "application/pdf") {
                      message.error("You can only upload PDF files!");
                      return Upload.LIST_IGNORE; // Prevent the file from being added to the list
                    }
                    if (!isLt10MB) {
                      message.error("PDF must be smaller than 50MB!");
                      return Upload.LIST_IGNORE;
                    }

                    // Trigger the uploadFile function directly
                    const cancelToken = axios.CancelToken.source();
                    setPdfUploads((prevUploads) => [
                      ...prevUploads,
                      {
                        file,
                        progress: 0,
                        cancelToken,
                        remainingTime: "Calculating...",
                        isUploading: false,
                      },
                    ]);

                    return false; // Prevent auto-upload by Upload component
                  }}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">Select PDFs to upload</p>
                  <p className="ant-upload-hint">
                    Maximum file size 50MB, format .pdf (Max 10 files).
                  </p>
                </Upload.Dragger>
              )}
            </Form.Item>
          )}
          {pdfUploads.length > 0 &&
            pdfUploads.some((upload) => upload.isUploading) && (
              <Card>
                {pdfUploads.map(
                  (upload: any, index: number) =>
                    upload.isUploading && (
                      <React.Fragment key={upload.file.name}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "flex-start",
                          }}
                        >
                          <Spin
                            size="large"
                            indicator={<LoadingOutlined spin />}
                          />
                          <div style={{ width: "100%", marginLeft: "10px" }}>
                            <Text>{upload.file.name}</Text>
                            {upload.progress > 0 && (
                              <div
                                style={{
                                  width: "100%",
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                }}
                              >
                                <Progress
                                  percent={upload.progress}
                                  status={
                                    upload.isUploading ? "active" : "success"
                                  }
                                  style={{ width: "100%" }}
                                />
                                <ANTDButton
                                  type="text"
                                  icon={<CloseOutlined />}
                                  onClick={() =>
                                    cancelUpload("pdf", upload.file.name)
                                  }
                                  style={{
                                    marginLeft: "10px",
                                  }}
                                ></ANTDButton>
                              </div>
                            )}
                          </div>
                        </div>
                        {index < pdfUploads.length - 1 && <Divider />}
                      </React.Fragment>
                    )
                )}
              </Card>
            )}
        </Form>
      </Drawer>

      {/* AssetViewerModal for viewing videos and PDFs */}
      <AssetViewerModal
        isOpen={isViewerModalOpen}
        onClose={closeViewerModal}
        contentType={viewerContentType}
        title={viewerTitle}
        assetId={lectureId}
        pdfPassword={pdfPassword}
      />
    </>
  );
};

export default UploadAssetDrawer;
